"use client";

import type { JSX } from "react";
import { useEffect, useRef } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { HERO_VIDEO_URL } from "@/lib/video-assets";
import { CONSULTATION_URL, WA_NUMBERS } from "@/lib/consultation-links";
import { openWhatsAppWithUtm } from "@/lib/whatsapp";
import { consultationCtaClassName } from "@/lib/cta-styles";

const consultationActions = [
  "Talk to a Specialist",
  "Book a Consultation Call",
];

export const HeroSection = (): JSX.Element => {
    const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Once a continuously-looping background video has been playing
    // off-screen for a while, some browsers evict its decode buffer to
    // reclaim memory — resuming playback then forces a full network
    // re-fetch of the entire file from byte 0 (confirmed via a real HAR
    // capture: the exact same multi-MB video re-downloaded from scratch
    // ~50s into a session, despite CloudFront marking it immutable/
    // cacheable for a year). Pausing while the section is well outside the
    // viewport avoids keeping that buffer "hot" for content nobody is
    // looking at, which sidesteps the eviction and is a sensible CPU/
    // battery saving either way.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { rootMargin: "200px 0px" },
    );

    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      aria-labelledby="faq-heading"
      className="relative flex w-full flex-col max-sm:h-[80vh] sm:min-h-screen justify-center items-center gap-14 px-4 pt-6 sm:px-6 overflow-hidden"
    >
      {/* preload="metadata" (not "auto"): "auto" is a hint that the browser
          can front-load this multi-megabyte file as eagerly as it likes,
          which competes for bandwidth with the JS bundle the rest of the
          page (including every scroll animation below) needs to hydrate —
          on a constrained connection the video can noticeably delay that.
          `autoPlay` still starts fetching enough to play as soon as the
          element mounts; this only lowers the browser's own scheduling
          priority for it relative to render-blocking script requests. */}
      <video
        ref={videoRef}
        className="absolute left-1/2 top-0 z-0 h-full w-[120%] max-w-none -translate-x-1/2 object-cover opacity-75"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
      >
        <source src={HERO_VIDEO_URL} type="video/mp4" />
      </video>

           <div className="relative z-10 w-full max-w-[1035px] flex flex-col items-center gap-8">
        <div className="flex w-full flex-col items-center gap-2">
              <h1 className="text-[34px] leading-[40px] sm:text-[48px] sm:leading-[54px] lg:text-[64px] lg:leading-[71px] font-semibold text-center [font-family:'Suisse_Intl_Trial-Regular',Helvetica]">Go Global by Forming a 0% Tax<br className="hidden sm:block"></br>
Hong Kong  <span className="bg-[radial-gradient(50%_50%_at_50%_50%,#0061FE_34.13%,#DAE8FF_100%)] bg-clip-text text-transparent [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-[34px] leading-[40px] sm:text-[48px] sm:leading-[54px] lg:text-[64px] lg:leading-[71px] italic font-normal">Remote Company</span> </h1>
          <p className="max-w-[850px] text-center text-[#595D71] text-[16px] leading-[24px] sm:text-[20px] sm:leading-[28px] [font-family:'Suisse_Intl_Trial-Regular',Helvetica]">MuteTaxes' registered professionals handle Hong Kong company setup, compliance, LLC formation and tax optimization correctly. We shield you from costly filing mistakes that can expose your personal assets.</p></div>
          <div className="w-full">
           <div className="flex w-full flex-col items-center gap-[21px]">
             <div
              id="hero-cta-buttons"
              className="flex w-full flex-col items-center gap-4 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-center"
            >
              {consultationActions.map((action) => {
                const className = `${consultationCtaClassName(action)} w-[70vw] sm:w-auto`;

                if (action === "Talk to a Specialist") {
                  return (
                    <Button
                      key={action}
                      type="button"
                      className={`cursor-pointer ${className}`}
                      onClick={() => openWhatsAppWithUtm(WA_NUMBERS, "home-v3")}
                    >
                      {action}
                    </Button>
                  );
                }

                if (action === "Book a Consultation Call") {
                  return (
                    <Button key={action} asChild className={className}>
                      <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer">
                        {action}
                      </a>
                    </Button>
                  );
                }

                return (
                  <Button key={action} type="button" className={className}>
                    {action}
                  </Button>
                );
              })}
            </div>
            <div className="flex items-center justify-center gap-5">
              <img
                className="w-[120px] h-5"
                alt="Frame"
                src="/home-v3/frame-122-1.png"
              />
              <p className="[font-family:'Suisse_Intl_Trial-Regular',Helvetica] whitespace-nowrap text-sm lg:text-[clamp(6px,1.25vw,18px)] font-normal leading-4 tracking-[0] text-[#585d70]">
                100+ Reviews
              </p>
            </div>
          </div>
          </div>
        </div>
    </section>
  );
};
